// Pipelines · Conversion (TripBook Activation, phase 5). Mirrors
// 51trips-colab-api docs/tripbook-pipelines-conversion-endpoint.md. Admin-only.
// Instants are epoch millis, trip dates UTC midnight, amounts in minor units.

import {ActivationChannel, Touchpoint} from './Activation.ts'
import {BoardFilters, CrmActor, HistoryPage, NextAction, PreparedMessage} from './Pipeline.ts'

// §1 — column order of the board, in its six collapsible groups.
export const CONVERSION_GROUPS = [
    {id: 'WAITING', stages: ['REGISTERED', 'WAITING_FOR_TRIP_END']},
    {id: 'PHOTOS', stages: ['NO_PHOTOS', 'PHOTOS_NOT_CONFIRMED', 'DIY_FLIPBOOK_READY', 'DIY_UNDER_30_PAGES', 'DIY_HELP_OFFERED']},
    {id: 'PRODUCTION', stages: ['IN_PRODUCTION', 'DETAILS_PENDING', 'DRAFT_READY']},
    {id: 'REVIEW', stages: ['WAITING_CUSTOMER_OK', 'EXTRA_PAGES_DECISION', 'CHANGES_51T_ADAPTING', 'CHANGES_ADAPTATION_READY', 'CHANGES_CUSTOMER_EDITING']},
    {id: 'PAYMENT_AND_PRINT', stages: ['PAYMENT_LINK_TO_SEND', 'AWAITING_PAYMENT', 'READY_TO_PRINT', 'SENT_TO_PRINT_SHOP', 'PRINTING_SHIPPING']},
    {id: 'CLOSED', stages: ['DELIVERED', 'LOST_NO_RESPONSE', 'LOST_NOT_INTERESTED', 'LOST_WRONG_CONTACT']},
] as const

export type ConversionGroup = typeof CONVERSION_GROUPS[number]['id']
export type ConversionStage = typeof CONVERSION_GROUPS[number]['stages'][number]
export const CONVERSION_STAGES: ConversionStage[] = CONVERSION_GROUPS.flatMap((g) => [...g.stages])

export type ConversionLostReason = 'NO_RESPONSE' | 'NOT_INTERESTED' | 'WRONG_CONTACT'
export const CONVERSION_LOST_REASONS: ConversionLostReason[] = ['NO_RESPONSE', 'NOT_INTERESTED', 'WRONG_CONTACT']

export const CONVERSION_LOST_STAGE_REASON: Partial<Record<ConversionStage, ConversionLostReason>> = {
    LOST_NO_RESPONSE: 'NO_RESPONSE',
    LOST_NOT_INTERESTED: 'NOT_INTERESTED',
    LOST_WRONG_CONTACT: 'WRONG_CONTACT',
}

export const isFinalConversionStage = (stage: ConversionStage | null | undefined) =>
    stage === 'DELIVERED' || (!!stage && stage.startsWith('LOST_'))

// §4 — SET_STAGE targets and the stages each one can be reached from. The
// board uses it to offer drop targets and "Move to…" before the card's detail
// (with its allowedStages) is loaded; the backend validates again (409).
export const SET_STAGE_FROM: Partial<Record<ConversionStage, ConversionStage[]>> = {
    DETAILS_PENDING: ['IN_PRODUCTION'],
    DRAFT_READY: ['IN_PRODUCTION', 'DETAILS_PENDING'],
    WAITING_CUSTOMER_OK: ['DRAFT_READY', 'CHANGES_ADAPTATION_READY'],
    CHANGES_51T_ADAPTING: ['WAITING_CUSTOMER_OK', 'EXTRA_PAGES_DECISION', 'CHANGES_CUSTOMER_EDITING'],
    CHANGES_ADAPTATION_READY: ['CHANGES_51T_ADAPTING'],
}

// Whether a card can be dropped on a column (contract §1 `drop` and §4
// SET_STAGE origins). The backend checks again and answers 409 otherwise.
export function conversionAccepts(card: ConversionCard, column: Pick<ConversionColumn, 'stage' | 'drop'> | undefined): boolean {
    if (!column?.drop || isFinalConversionStage(card.crmStage)) return false
    if (column.drop.command === 'MARK_LOST') return true
    return !!SET_STAGE_FROM[column.stage]?.includes(card.crmStage)
}

export type PendingReason = 'TOO_FEW_PHOTOS' | 'TOO_MANY_PHOTOS' | 'MISSING_INFO'
export const PENDING_REASONS: PendingReason[] = ['TOO_FEW_PHOTOS', 'TOO_MANY_PHOTOS', 'MISSING_INFO']

export type ExtraPagesDecision = 'PAYS' | 'REDUCES' | 'NONE'
export const EXTRA_PAGES_DECISIONS: ExtraPagesDecision[] = ['PAYS', 'REDUCES', 'NONE']

export type ConversionOutcome = 'SENT' | 'REPLIED' | 'NO_ANSWER' | 'NOT_INTERESTED' | 'WRONG_CONTACT'
export const CONVERSION_OUTCOMES: ConversionOutcome[] = ['SENT', 'REPLIED', 'NO_ANSWER', 'NOT_INTERESTED', 'WRONG_CONTACT']

export type PrintChannel = '51T' | 'SUP' | 'B2C'
export const PRINT_CHANNELS: PrintChannel[] = ['51T', 'SUP', 'B2C']

export type ConversionMode = 'EXPERTS' | 'DIY'
export type ConversionChannel = 'B2C' | 'AGENCY'
export type PaymentStatus = 'NONE' | 'LINK_CREATED' | 'PAID' | 'FAILED'

// §1.2 — alerts are badges; ON_HOLD and AGENCY_REVIEW mirror the flags.
export type ConversionAlert =
    | 'ON_HOLD' | 'AGENCY_REVIEW' | 'NO_REPLY' | 'TOO_FEW_PHOTOS' | 'TOO_MANY_PHOTOS' | 'MISSING_INFO'
    | 'PAYMENT_FAILED' | 'NO_TRIP_DATES' | 'UNVERIFIED' | 'PRICING_PLACEHOLDER' | 'CONTACT_FAILED' | string

export type OnHoldFlag = {until: number; note?: string | null; at?: number | null; by?: string | null}

// §2
export type ConversionCard = {
    id: string
    pipeline: 'conversion'
    crmStage: ConversionStage
    crmGroup: ConversionGroup
    crmStageAt: number
    tripId: string | null
    sessionId: string | null
    activationId: string | null
    invitationCode: string | null
    market: string
    channel: ConversionChannel | null
    mode: ConversionMode | null
    agencyId: string | null
    agencyName: string | null
    accountManagerId: string | null
    traveler: {
        firstName?: string | null
        lastName?: string | null
        email?: string | null
        phone?: string | null
        preferredChannel?: ActivationChannel | null
    }
    trip: {startDate: number | null; endDate: number | null; countries: string[]}
    editorId: string | null
    pages: {uploaded: number | null; included: number | null; extra: number | null}
    extraPagesDecision: ExtraPagesDecision | null
    payment: {status: PaymentStatus; amountMinor: number | null; currency: string | null}
    pendingReason: PendingReason | null
    flags: {onHold: OnHoldFlag | null; agencyReview: boolean}
    nextAction: NextAction | null
    alerts: ConversionAlert[]
    messagesSent: number
    lostReason: ConversionLostReason | null
    shortcuts: ConversionShortcuts
}

export type ConversionShortcuts = {flipbookUrl: string | null; paymentUrl: string | null; trackingUrl: string | null}

export type ConversionDrop =
    | {command: 'SET_STAGE'; reason?: null; needs: string[]}
    | {command: 'MARK_LOST'; reason: ConversionLostReason; needs: string[]}

// §3.1
export type ConversionColumn = {
    stage: ConversionStage
    group: ConversionGroup
    type: 'EVENT' | 'TIME' | 'DATA' | 'FINAL'
    actor: CrmActor
    final: boolean
    drop: ConversionDrop | null
    count: number
    cards: ConversionCard[]
    nextCursor: string | null
}

export type ConversionBoardResponse = {
    pipeline: 'conversion'
    generatedAt: number
    groups: {id: ConversionGroup; name: string; count: number}[]
    columns: ConversionColumn[]
}

// Same filters as Activation minus the route, plus mode, editor (uids and/or
// 'unassigned', several values as CSV like market and team), channel and group.
export type ConversionBoardFilters = Omit<BoardFilters, 'route'> & {
    mode?: ConversionMode
    editor?: string[]
    channel?: ConversionChannel
    group?: ConversionGroup
}

export type ConversionBoardQuery = ConversionBoardFilters & {stage?: ConversionStage; cursor?: string; limit?: number}

export type ConversionCommandType =
    | 'SET_STAGE' | 'SET_PENDING_REASON' | 'TOUCHPOINT_DONE' | 'APPROVE_DRAFT' | 'SET_EXTRA_PAGES_DECISION'
    | 'SET_PAGES' | 'CREATE_PAYMENT_LINK' | 'HOLD' | 'RESUME' | 'ASSIGN_EDITOR' | 'SET_FLIPBOOK'
    | 'SET_AGENCY_REVIEW' | 'MARK_SENT_TO_PRINT' | 'MARK_SHIPPED' | 'MARK_DELIVERED' | 'MARK_LOST'
    | 'OVERRIDE_STAGE' | 'ADD_FOLLOW_UP' | 'ASSIGN' | 'ADD_NOTE' | 'UPDATE_CONTACT'

// §3.2
export type CrmConversionDetail = {
    conversion: ConversionCard & {
        touchpoints: Touchpoint[]
        invitationCodeId: string | null
        userId: string | null
        pipedriveDealId: string | number | null
        validated: boolean
        registeredAt: number | null
        lastResponseAt: number | null
        stageEnteredAt: Partial<Record<ConversionStage, number>> | null
        createdAt: number | null
        updatedAt: number | null
    }
    crm: {
        pipeline: 'CONVERSION'
        stage: ConversionStage
        group: ConversionGroup
        stageAt: number
        nextAction: NextAction | null
        alerts: ConversionAlert[]
        messagesSent: number
        lostReason: ConversionLostReason | null
        lostAt: number | null
        lostNote: string | null
        override: {stage: ConversionStage; note: string; at?: number; by?: string} | null
    }
    agency: {
        id: string
        name: string | null
        accountManagerId: string | null
        accountManagerName: string | null
        optInPreTrip: boolean
    } | null
    tripBook: {
        mode: ConversionMode | null
        pagesUploaded: number | null
        includedPages: number | null
        extraPages: number | null
        editorId: string | null
        editorName: string | null
        flipbookUrl: string | null
        draftsSent: number | null
        draftSentAt: number | null
        draftApprovedAt: number | null
        pendingReason: PendingReason | null
        printChannel: PrintChannel | null
    } | null
    extraPages: {
        pages: number | null
        included: number | null
        extra: number | null
        currency: string | null
        amountMinor: number | null
        placeholder: boolean
        decision: ExtraPagesDecision | null
        decisionAt: number | null
        breakdown: {fromPage: number; toPage: number; pages: number; unitMinor: number; subtotalMinor: number}[]
    } | null
    payment: {
        status: PaymentStatus
        url: string | null
        amountMinor: number | null
        currency: string | null
        linkCreatedAt: number | null
        paidAt: number | null
        failedAt: number | null
    } | null
    print: {
        sentToPrintAt: number | null
        shippedAt: number | null
        deliveredAt: number | null
        trackingUrl: string | null
        printChannel: PrintChannel | null
    } | null
    shortcuts: ConversionShortcuts | null
    nextTouch: {
        touchpoint: Touchpoint
        position: null
        preparedMessage: PreparedMessage | null
    } | null
    history: HistoryPage
    allowedCommands: ConversionCommandType[]
    allowedStages: ConversionStage[]
    pendingReasons: PendingReason[]
    lostReasons: ConversionLostReason[]
    outcomes: ConversionOutcome[]
    decisions: ExtraPagesDecision[]
}

type Base = {commandId?: string; note?: string}

// §4 — the body of POST /crm/conversions/{id}/commands.
export type ConversionCommand =
    | Base & {type: 'SET_STAGE'; data: {stage: ConversionStage; reason?: PendingReason; pages?: number}}
    | Base & {type: 'SET_PENDING_REASON'; data: {reason: PendingReason}}
    | Base & {type: 'TOUCHPOINT_DONE'; data: {touchpointId?: string; channel?: ActivationChannel; outcome?: ConversionOutcome; pages?: number}}
    | Base & {type: 'APPROVE_DRAFT'; data?: Record<string, never>}
    | Base & {type: 'SET_EXTRA_PAGES_DECISION'; data: {decision: ExtraPagesDecision}}
    | Base & {type: 'SET_PAGES'; data: {pages: number}}
    | Base & {type: 'CREATE_PAYMENT_LINK'; data?: {regenerate?: boolean}}
    | Base & {type: 'HOLD'; data: {date: number; note?: string}}
    | Base & {type: 'RESUME'; data?: Record<string, never>}
    | Base & {type: 'ASSIGN_EDITOR'; data: {editorId: string | null}}
    | Base & {type: 'SET_FLIPBOOK'; data: {url: string}}
    | Base & {type: 'SET_AGENCY_REVIEW'; data: {value: boolean}}
    | Base & {type: 'MARK_SENT_TO_PRINT'; data?: {printChannel?: PrintChannel}}
    | Base & {type: 'MARK_SHIPPED'; data?: {trackingUrl?: string}}
    | Base & {type: 'MARK_DELIVERED'; data?: Record<string, never>}
    | Base & {type: 'MARK_LOST'; data: {reason: ConversionLostReason}}
    | Base & {type: 'OVERRIDE_STAGE'; note: string; data: {stage: ConversionStage}}
    | Base & {type: 'ADD_FOLLOW_UP'; data: {date: number; channel: ActivationChannel; assignee?: string}}
    | Base & {type: 'ASSIGN'; data: {touchpointId?: string; assignee: string | null}}
    | Base & {type: 'ADD_NOTE'; data: {text: string}}
    | Base & {type: 'UPDATE_CONTACT'; data: {email?: string | null; phone?: string | null; preferredChannel?: ActivationChannel | '' | null}}

// §5
export const CRM_ERROR_PRICING_NOT_CONFIGURED = 'PRICING_NOT_CONFIGURED'
export const CRM_ERROR_PAYMENT_PROVIDER = 'PAYMENT_PROVIDER_ERROR'
